import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Table, Form, Button } from "react-bootstrap";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import TablePagination from "../pagination/TablePagination";
import { useGetClassroomQuizLimitInfo } from "../../../../hooks/quiz/useGetClassroomQuizLimitInfo";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import classRoomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import "../../../../common/styles/table.css";
import "../classroom/Classroom.css";
import BackButton from "../Goback/GoBack";
import AssignQuizLimitModal from "./AssignQuizLimitModal";


/**
 * QuizLimitInfo - Displays student quiz attempt details for a specific classroom
 */
const QuizLimitInfo = () => {
  const { id: classroomId } = useParams();
  const location = useLocation();
  const classroomName =
    location.state?.classroomName || "Classroom Quiz Info";

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [exhaustedFilter, setExhaustedFilter] = useState("all");

  const { loading, error, students, meta, getClassroomQuizLimitInfo } =
    useGetClassroomQuizLimitInfo();

  const fetchStudents = useCallback(
    (page) => {
      if (classroomId) {
        getClassroomQuizLimitInfo({
          classroomId,
          page,
          per_page: ITEMS_PER_PAGE,
          ...(exhaustedFilter !== "all" && {
            exhausted: exhaustedFilter === "exhausted" ? 1 : 0,
          }),
        });
      }
    },
    [classroomId, exhaustedFilter]
  );

  useEffect(() => {
    fetchStudents(currentPage);
  }, [currentPage, exhaustedFilter]);

  const handleRefresh = () => {
    fetchStudents(currentPage);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = [...selectedStudents];
      students.forEach((student) => {
        if (!newSelected.find((s) => s.student_id === student.student_id)) {
          newSelected.push(student);
        }
      });
      setSelectedStudents(newSelected);
    } else {
      const newSelected = selectedStudents.filter(
        (s) => !students.find((st) => st.student_id === s.student_id)
      );
      setSelectedStudents(newSelected);
    }
  };

  const handleSelectStudent = (student, checked) => {
    if (checked) {
      setSelectedStudents([...selectedStudents, student]);
    } else {
      setSelectedStudents(
        selectedStudents.filter((s) => s.student_id !== student.student_id)
      );
    }
  };

  const isAllSelectedInPage =
    students?.length > 0 &&
    students.every((s) =>
      selectedStudents.find((st) => st.student_id === s.student_id)
    );


  const totalPages = meta?.last_page || 1;

  // Show skeleton on initial load
  if (loading && students === undefined) {
    return (
      <div className="classroom-cnt">
        <div className="classroom-container">
          <div className="pb-0">
            <BackButton />
          </div>
          <img src={classRoomImg} className="img" alt="Quiz" />
          <h2 className="text">{classroomName}</h2>
        </div>
        <div className="pt-2 d-flex justify-content-between">
          <Form.Select
            style={{ width: "200px" }}
            value={exhaustedFilter}
            onChange={(e) => {
              setExhaustedFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="exhausted">Exhausted</option>
            <option value="not_exhausted">Not Exhausted</option>
          </Form.Select>
          <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
        </div>
        <TableSkeleton rows={ITEMS_PER_PAGE} columns={7} />
      </div>
    );
  }

  // Show error state
  if (error || students === null) {
    return (
      <div className="classroom-cnt">
        <div className="classroom-container">
          <div className="pb-0">
            <BackButton />
          </div>
          <img src={classRoomImg} className="img" alt="Quiz" />
          <h2 className="text">{classroomName}</h2>
        </div>
        <div className="pt-2 d-flex justify-content-between">
          <Form.Select
            style={{ width: "200px" }}
            value={exhaustedFilter}
            onChange={(e) => {
              setExhaustedFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="exhausted">Exhausted</option>
            <option value="not_exhausted">Not Exhausted</option>
          </Form.Select>
          <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
        </div>
        <TableError
          error={error}
          handleRefresh={handleRefresh}
          loading={loading}
        />
      </div>
    );
  }

  const hasStudents = students && students.length > 0;

  return (
    <div className="classroom-cnt">
      <BackButton />
      <div className="classroom-container">
        <img src={classRoomImg} className="img" alt="Quiz" />
        <h2 className="text">{classroomName}</h2>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-0 mt-3">
        <div className="d-flex gap-3 align-items-center">
          <Button
            className="custom-primary-button"
            onClick={() => setShowAssignModal(true)}
            disabled={selectedStudents.length === 0}
          >
            Assign Quiz Limit
            {selectedStudents.length > 0 ? ` (${selectedStudents.length})` : ""}
          </Button>

          <Form.Select
            style={{ width: "200px" }}
            value={exhaustedFilter}
            onChange={(e) => {
              setExhaustedFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">All</option>
            <option value="exhausted">Exhausted</option>
            <option value="not_exhausted">Not Exhausted</option>
          </Form.Select>
        </div>
        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {!hasStudents ? (
        <div className="alert alert-info mt-3" role="alert">
          No students found in this classroom.
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th style={{ width: "40px" }} className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={isAllSelectedInPage}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Username</th>
                  <th className="text-center">Limit</th>
                  <th className="text-center">Used Attempts</th>
                  <th className="text-center">Remaining</th>
                  {/* <th className="text-center">Usage %</th>
                  <th className="text-center">Addon Available</th> */}
                </tr>
              </thead>
              <tbody>
                {students.map((student) => {
                  const isSelected = !!selectedStudents.find(
                    (s) => s.student_id === student.student_id
                  );
                  return (
                    <tr key={student.student_id}>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) =>
                            handleSelectStudent(student, e.target.checked)
                          }
                        />
                      </td>
                      <td>{student.name || "-"}</td>
                      <td>{student.username || "-"}</td>
                      <td className="text-center">
                        {student.limit !== null ? student.limit : "Unlimited"}
                      </td>
                      <td className="text-center">{student.used_attempts ?? 0}</td>
                      <td className="text-center">
                        {student.remaining_attempts !== null
                          ? student.remaining_attempts
                          : "Unlimited"}
                      </td>
                      {/* <td className="text-center">
                      {student.attempt_usage_percent !== null
                        ? `${student.attempt_usage_percent}%`
                        : "-"}
                    </td>
                    <td className="text-center">
                      {student.addon_available ? (
                        <span className="badge bg-success">Yes</span>
                      ) : (
                        <span className="badge bg-secondary">No</span>
                      )}
                    </td> */}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}

      {showAssignModal && (
        <AssignQuizLimitModal
          showModal={showAssignModal}
          handleClose={() => setShowAssignModal(false)}
          selectedStudents={selectedStudents}
          classroomId={classroomId}
          onSuccessCallback={() => {
            handleRefresh();
            setSelectedStudents([]);
          }}
        />
      )}
    </div>
  );
};

export default QuizLimitInfo;
